/**
 * Deterministic verification phrase generator.
 * Given the same input string (base64url fragment), always produces
 * the same human-readable 6-word phrase. This lets the user visually
 * confirm the link is intact without comparing raw base64.
 *
 * Pattern: ADJ NOUN VERB ADJ NOUN VERB
 * (two short clauses, easy to read aloud)
 */

const ADJECTIVES = [
  'amber', 'ancient', 'azure', 'blazing', 'broken', 'calm', 'chilly', 'copper',
  'cosmic', 'crimson', 'crystal', 'cyber', 'dark', 'deep', 'digital', 'dim',
  'distant', 'dusk', 'echo', 'electric', 'empty', 'endless', 'eternal', 'fading',
  'fallen', 'fierce', 'finite', 'flicker', 'floating', 'frosty', 'frozen', 'fuzzy',
  'glowing', 'golden', 'green', 'grey', 'hollow', 'hot', 'icy', 'infinite',
  'inner', 'inverted', 'jade', 'jagged', 'keen', 'laser', 'late', 'layered',
  'liquid', 'lost', 'lunar', 'mellow', 'misty', 'molten', 'murky', 'mystic',
  'neon', 'noble', 'noisy', 'oblique', 'odd', 'orbital', 'pale', 'phantom',
  'phasic', 'pixel', 'plasma', 'pulsing', 'pure', 'quantum', 'quiet', 'radiant',
  'rapid', 'raw', 'razor', 'red', 'restless', 'rising', 'rough', 'royal',
  'rugged', 'rust', 'serene', 'sharp', 'signal', 'silent', 'silver', 'sinister',
  'slow', 'solar', 'sonic', 'spectral', 'spinning', 'static', 'steady', 'still',
  'storm', 'strange', 'sub', 'super', 'swift', 'thin', 'turbid', 'twilight',
  'ultra', 'unknown', 'unstable', 'vacant', 'vast', 'vibrant', 'violet', 'viral',
  'void', 'warm', 'warped', 'wave', 'white', 'wild', 'wispy', 'woven',
  'zero', 'zippy', 'zonal', 'zenith', 'binary', 'scattered', 'drifting', 'hollow',
];

const NOUNS = [
  'algorithm', 'arc', 'atom', 'beam', 'beat', 'binary', 'bit', 'bloom',
  'burst', 'byte', 'carrier', 'cascade', 'cave', 'channel', 'chord', 'circuit',
  'clock', 'cloud', 'cluster', 'code', 'coil', 'comet', 'compass', 'core',
  'crystal', 'cycle', 'datum', 'delta', 'depth', 'dimension', 'dream', 'drift',
  'drone', 'drum', 'dune', 'echo', 'edge', 'entropy', 'field', 'filter',
  'flame', 'flare', 'flash', 'flux', 'forge', 'form', 'fragment', 'frame',
  'frequency', 'gate', 'ghost', 'glitch', 'glyph', 'grid', 'ground', 'harmony',
  'hash', 'heap', 'helix', 'horizon', 'hum', 'impact', 'impulse', 'index',
  'kernel', 'key', 'latch', 'lattice', 'layer', 'lens', 'light', 'loop',
  'machine', 'mask', 'matrix', 'mesh', 'mirror', 'mode', 'modulus', 'moment',
  'motion', 'node', 'noise', 'note', 'null', 'orbit', 'oscillator', 'output',
  'packet', 'particle', 'patch', 'path', 'pattern', 'phase', 'photon', 'pixel',
  'plane', 'plateau', 'point', 'port', 'probe', 'pulse', 'query', 'quasar',
  'register', 'relay', 'resonance', 'rhythm', 'ring', 'root', 'sample', 'sequence',
  'shift', 'signal', 'sink', 'source', 'space', 'spectrum', 'spiral', 'stream',
  'string', 'struct', 'surface', 'surge', 'symbol', 'sync', 'terrain', 'threshold',
  'tide', 'token', 'trace', 'track', 'transit', 'tree', 'trigger', 'tunnel',
  'vector', 'void', 'vortex', 'warp', 'wave', 'wire', 'zone', 'zenith',
];

const VERBS = [
  'bounces', 'burns', 'cascades', 'chirps', 'clicks', 'collides', 'computes',
  'crawls', 'cycles', 'drifts', 'echoes', 'emits', 'encodes', 'escapes',
  'fades', 'flickers', 'floats', 'flows', 'folds', 'fragments', 'glows',
  'hums', 'ignites', 'initializes', 'inverts', 'iterates', 'jitters', 'loops',
  'modulates', 'morphs', 'multiplies', 'oscillates', 'overflows', 'pings',
  'propagates', 'pulses', 'refracts', 'resonates', 'reverbs', 'ripples',
  'rotates', 'samples', 'scatters', 'shifts', 'shimmers', 'sings', 'slides',
  'snaps', 'spirals', 'stacks', 'sweeps', 'throbs', 'ticks', 'transmits',
  'trembles', 'triggers', 'truncates', 'tunnels', 'vibrates', 'warps', 'wraps',
  'xors', 'zaps', 'zeroes', 'blooms', 'merges', 'branches', 'descends',
  'expands', 'fractures', 'halts', 'leaks', 'mounts', 'pierces', 'reflects',
  'scans', 'spawns', 'surges', 'traces', 'unwinds', 'vaults', 'weaves',
];

/** FNV-1a-like hash — fast, deterministic, good avalanche. */
function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (Math.imul(h, 0x01000193) >>> 0);
  }
  return h >>> 0;
}

function pick<T>(arr: T[], seed: number): T {
  return arr[seed % arr.length];
}

/**
 * Generate a deterministic 6-word verification phrase from a base64url fragment.
 * Pattern: ADJ NOUN VERB · ADJ NOUN VERB
 * Same fragment → always the same phrase.
 */
export function generateVerificationPhrase(fragment: string): string {
  if (!fragment) return '— — — · — — —';

  // Six independent hashes derived from the fragment using different salts
  const h1 = hashString(fragment + '\x01');
  const h2 = hashString(fragment + '\x02');
  const h3 = hashString(fragment + '\x03');
  const h4 = hashString(fragment + '\x04');
  const h5 = hashString(fragment + '\x05');
  const h6 = hashString(fragment + '\x06');

  const adj1  = pick(ADJECTIVES, h1);
  const noun1 = pick(NOUNS,      h2);
  const verb1 = pick(VERBS,      h3);
  const adj2  = pick(ADJECTIVES, h4);
  const noun2 = pick(NOUNS,      h5);
  const verb2 = pick(VERBS,      h6);

  return `${adj1} ${noun1} ${verb1} · ${adj2} ${noun2} ${verb2}`;
}
